/**
 * NetSpikeAnalyzer — surfaces WHAT caused a network spike + HOW BAD it was.
 *
 * Sibling of NetTrafficOverlay + NetobsPacketSparkline. Walks
 * window.nm.tracer.recentTraffic on the 250ms tick, groups events into
 * 1-second buckets WITH per-message-type breakdown (sparkline + traffic
 * overlay only carry totals), maintains a rolling 30s baseline, and
 * detects + records spike events.
 *
 * Spike severity tiers (vs rolling baseline of last 30s):
 *   WARMING   — baseline window not yet filled (cold start)
 *   NORMAL    — under 1.5×
 *   ELEVATED  — 1.5×–3×
 *   SPIKE     — 3×–5×
 *   SEVERE    — 5×–10×
 *   CRITICAL  — over 10× OR over 1MB/s absolute
 *
 * Two guard rails prevent false-positive spikes:
 *   1. WARMING tier shown until ≥MIN_WARMUP_BUCKETS of the rolling window
 *      have non-zero traffic. Without this, the very first packet of a
 *      fresh session divides by a near-zero median baseline and instantly
 *      lights up CRITICAL — the "extreme spike" cold-start false positive.
 *   2. Buckets under MIN_SPIKE_BYTES are skipped for both current-tier
 *      classification and historical recording. A bucket with 80 bytes
 *      isn't a meaningful spike no matter what its ratio says.
 *
 * Also draws absolute budget lines (3 KB/s default) for context.
 *
 * UI: shows current state + last 5 spike events with timestamp,
 * direction (▲↑ out / ▼↓ in), severity tier, and top 3 message types
 * in that bucket (the actual offenders).
 *
 * Gating: ?debug=1, ?net=1, ?spikes=1, or window.forceDebug.
 * Position: top-right, top:560 (under NetTrafficOverlay).
 */

import React, { useEffect, useRef, useState } from 'react';
import { NETOBS_COLORS } from '../netobs/colors';
import { CopyButton } from '../debug/CopyButton';

const POLL_INTERVAL_MS = 250;
const BUCKET_MS = 1000;
const BASELINE_WINDOW_SECS = 30;
const SPIKE_HISTORY_MAX = 8;
const ABSOLUTE_BUDGET_BPS = 3000;
const CRITICAL_ABSOLUTE_BPS = 1024 * 1024;  // 1 MB/s

// Of the 28 baseline buckets, require at least this many to carry traffic.
// The classifier additionally requires a non-zero rolling median: ten
// non-empty buckets can still leave the 28-bucket median at zero.
const MIN_WARMUP_BUCKETS = 10;

// Skip buckets under this size from spike classification entirely.
// Floor for "ratio noise" when both current + baseline are tiny.
const MIN_SPIKE_BYTES = 500;

type Tier = 'WARMING' | 'NORMAL' | 'ELEVATED' | 'SPIKE' | 'SEVERE' | 'CRITICAL';

const TIER_COLORS: Record<Tier, string> = {
  WARMING: '#7080a0',
  NORMAL: NETOBS_COLORS.HEALTH_OK,
  ELEVATED: NETOBS_COLORS.HEALTH_WARN,
  SPIKE: '#ff9f3f',
  SEVERE: NETOBS_COLORS.HEALTH_BAD,
  CRITICAL: '#ff3030',
};

const TIER_ICONS: Record<Tier, string> = {
  WARMING: '⏳',
  NORMAL: '🟢',
  ELEVATED: '🟡',
  SPIKE: '🟠',
  SEVERE: '🔴',
  CRITICAL: '⚠️',
};

interface TrafficEvent {
  seq?: number;
  timestamp: string;
  direction: string;
  type: string;
  bytes: number;
}

interface BucketBreakdown {
  bytesIn: number;
  bytesOut: number;
  msgsIn: number;
  msgsOut: number;
  inTypes: Map<string, { bytes: number; msgs: number }>;
  outTypes: Map<string, { bytes: number; msgs: number }>;
}

interface SpikeEvent {
  ts: number;            // bucket end-time epoch ms
  direction: 'in' | 'out';
  bytes: number;
  msgs: number;
  ratio: number;         // bytes / baseline
  tier: Tier;
  topTypes: { type: string; bytes: number; msgs: number }[];
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n.toFixed(0)}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K`;
  return `${(n / 1024 / 1024).toFixed(2)}M`;
}

function classifyTier(ratio: number, absoluteBps: number): Tier {
  if (absoluteBps >= CRITICAL_ABSOLUTE_BPS) return 'CRITICAL';
  if (ratio >= 10) return 'CRITICAL';
  if (ratio >= 5) return 'SEVERE';
  if (ratio >= 3) return 'SPIKE';
  if (ratio >= 1.5) return 'ELEVATED';
  return 'NORMAL';
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function fmtRelTime(ts: number, now: number): string {
  const dSec = Math.floor((now - ts) / 1000);
  if (dSec < 5) return 'just now';
  if (dSec < 60) return `${dSec}s ago`;
  const dMin = Math.floor(dSec / 60);
  return `${dMin}m ago`;
}

export const NetSpikeAnalyzerContent: React.FC = () => {
  const [currentTier, setCurrentTier] = useState<Tier>('NORMAL');
  const [currentBpsIn, setCurrentBpsIn] = useState(0);
  const [currentBpsOut, setCurrentBpsOut] = useState(0);
  const [baseline, setBaseline] = useState({ in: 0, out: 0 });
  const [spikes, setSpikes] = useState<SpikeEvent[]>([]);
  const [, setTick] = useState(0);

  // Track which buckets we've already analyzed (by tEnd) to avoid double-report
  const analyzedBucketsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const tickFn = () => {
      const tracer = (window as any).nm?.tracer;
      if (!tracer || !Array.isArray(tracer.recentTraffic)) return;
      const events = tracer.recentTraffic as TrafficEvent[];

      // Build per-second buckets covering the last BASELINE_WINDOW_SECS.
      const now = Date.now();
      const buckets = new Map<number, BucketBreakdown>();
      for (let i = 0; i < BASELINE_WINDOW_SECS; i++) {
        const tEnd = Math.floor(now / BUCKET_MS) * BUCKET_MS - i * BUCKET_MS;
        buckets.set(tEnd, {
          bytesIn: 0, bytesOut: 0, msgsIn: 0, msgsOut: 0,
          inTypes: new Map(), outTypes: new Map(),
        });
      }
      const oldestBucketEnd = Math.min(...buckets.keys());

      for (const ev of events) {
        const ts = Date.parse(ev.timestamp);
        if (!Number.isFinite(ts)) continue;
        if (ts < oldestBucketEnd - BUCKET_MS || ts > now) continue;
        // Round up to bucket end
        const tEnd = Math.ceil(ts / BUCKET_MS) * BUCKET_MS;
        const b = buckets.get(tEnd);
        if (!b) continue;

        const isOut = ev.direction === 'client->server';
        const targetMap = isOut ? b.outTypes : b.inTypes;
        const cur = targetMap.get(ev.type) || { bytes: 0, msgs: 0 };
        cur.bytes += ev.bytes || 0;
        cur.msgs += 1;
        targetMap.set(ev.type, cur);
        if (isOut) {
          b.bytesOut += ev.bytes || 0;
          b.msgsOut += 1;
        } else {
          b.bytesIn += ev.bytes || 0;
          b.msgsIn += 1;
        }
      }

      // Compute baseline = median of bytes/sec across all buckets except the
      // newest 2 (so an in-progress bucket doesn't drag baseline down).
      const sortedEnds = Array.from(buckets.keys()).sort((a, b) => b - a);
      const baselineBuckets = sortedEnds.slice(2).map(t => buckets.get(t)!);
      const baselineIn = median(baselineBuckets.map(b => b.bytesIn));
      const baselineOut = median(baselineBuckets.map(b => b.bytesOut));
      setBaseline({ in: baselineIn, out: baselineOut });

      // Warmup gate: count baseline buckets that carry traffic in either
      // direction. While the rolling window is mostly empty, median is 0
      // and any traffic at all produces astronomical ratios → false CRITICAL.
      const nonZeroBuckets = baselineBuckets.reduce(
        (n, b) => n + ((b.bytesIn + b.bytesOut) > 0 ? 1 : 0),
        0,
      );
      const baselineHasSignal = baselineIn + baselineOut > 0;
      const warming = nonZeroBuckets < MIN_WARMUP_BUCKETS || !baselineHasSignal;

      // Current state = newest fully-completed bucket (sortedEnds[1]).
      const currentBucket = buckets.get(sortedEnds[1]);
      if (currentBucket) {
        setCurrentBpsIn(currentBucket.bytesIn);
        setCurrentBpsOut(currentBucket.bytesOut);
        const totalCurrent = currentBucket.bytesIn + currentBucket.bytesOut;
        if (warming) {
          setCurrentTier('WARMING');
        } else if (totalCurrent < MIN_SPIKE_BYTES) {
          // Sub-floor traffic: meaningful ratios can't exist, force NORMAL.
          setCurrentTier('NORMAL');
        } else {
          const totalBaseline = Math.max(1, baselineIn + baselineOut);
          const ratio = totalCurrent / totalBaseline;
          setCurrentTier(classifyTier(ratio, totalCurrent));
        }
      }

      // Detect new spike events in any bucket older than current but within
      // window. Skip already-analyzed buckets. Skip everything while warming
      // (baseline isn't trustworthy yet).
      const newSpikes: SpikeEvent[] = [];
      if (!warming) {
        for (const tEnd of sortedEnds.slice(1)) {  // skip in-progress newest
          if (analyzedBucketsRef.current.has(tEnd)) continue;
          const b = buckets.get(tEnd);
          if (!b) continue;
          // Mark as analyzed regardless of spike outcome
          analyzedBucketsRef.current.add(tEnd);

          for (const dir of ['in', 'out'] as const) {
            const bytes = dir === 'in' ? b.bytesIn : b.bytesOut;
            const msgs = dir === 'in' ? b.msgsIn : b.msgsOut;
            const baseDir = dir === 'in' ? baselineIn : baselineOut;
            if (bytes < MIN_SPIKE_BYTES) continue;  // absolute floor
            const ratio = bytes / Math.max(1, baseDir);
            const tier = classifyTier(ratio, bytes);
            if (tier === 'NORMAL') continue;  // only log ELEVATED+
            const typeMap = dir === 'in' ? b.inTypes : b.outTypes;
            const topTypes = Array.from(typeMap.entries())
              .map(([type, v]) => ({ type, bytes: v.bytes, msgs: v.msgs }))
              .sort((a, c) => c.bytes - a.bytes)
              .slice(0, 3);
            newSpikes.push({
              ts: tEnd,
              direction: dir,
              bytes,
              msgs,
              ratio,
              tier,
              topTypes,
            });
          }
        }
      }
      // Trim analyzed set if it gets large
      if (analyzedBucketsRef.current.size > 200) {
        const arr = Array.from(analyzedBucketsRef.current).sort((a, b) => b - a).slice(0, 100);
        analyzedBucketsRef.current = new Set(arr);
      }

      if (newSpikes.length > 0) {
        setSpikes(prev => {
          const combined = [...newSpikes, ...prev]
            .sort((a, b) => b.ts - a.ts)
            .slice(0, SPIKE_HISTORY_MAX);
          return combined;
        });
      }

      setTick(t => t + 1);  // refresh relative timestamps
    };

    tickFn();
    const id = setInterval(tickFn, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const tierColor = TIER_COLORS[currentTier];
  const totalCurrent = currentBpsIn + currentBpsOut;
  const totalBaseline = baseline.in + baseline.out;
  const ratioStr = totalBaseline > 0 ? `${(totalCurrent / totalBaseline).toFixed(1)}×` : '—';
  const now = Date.now();

  return (
    <div
      data-testid="net-spike-analyzer"
      data-tier={currentTier}
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 11,
        lineHeight: 1.4,
        padding: '4px 6px',
        borderLeft: `2px solid ${currentTier === 'NORMAL' ? '#2a2f3a' : tierColor}`,
        userSelect: 'none',
      }}
    >
      <div style={{
        color: '#7af',
        marginBottom: 4,
        letterSpacing: '0.08em',
        fontSize: 11,
        fontWeight: 600,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>SPIKE ANALYZER</span>
        <span style={{ color: tierColor, fontWeight: 700 }}>
          {TIER_ICONS[currentTier]} {currentTier}
        </span>
        <CopyButton getText={() => {
          const lines = [
            `SPIKE ANALYZER @ ${new Date().toISOString()}`,
            `current tier: ${currentTier}`,
            `now: ↓ ${fmtBytes(currentBpsIn)}/s · ↑ ${fmtBytes(currentBpsOut)}/s (${ratioStr} baseline)`,
            `baseline: ↓ ${fmtBytes(baseline.in)}/s · ↑ ${fmtBytes(baseline.out)}/s`,
            `spikes captured: ${spikes.length}`,
            ...spikes.slice(-10).map(s => `  ts=${fmtTime(s.ts)}  dir=${s.direction}  bytes=${s.bytes}  ratio=${s.ratio.toFixed(1)}× tier=${s.tier}`),
          ];
          return lines.join('\n');
        }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 6, fontSize: 10 }}>
        <span style={{ color: '#888' }}>now</span>
        <span>↓ {fmtBytes(currentBpsIn)}/s · ↑ {fmtBytes(currentBpsOut)}/s ({ratioStr} baseline)</span>

        <span style={{ color: '#888' }}>baseline</span>
        <span>↓ {fmtBytes(baseline.in)}/s · ↑ {fmtBytes(baseline.out)}/s (median {BASELINE_WINDOW_SECS}s)</span>

        <span style={{ color: '#888' }}>budget</span>
        <span>{fmtBytes(ABSOLUTE_BUDGET_BPS)}/s sustained · {fmtBytes(CRITICAL_ABSOLUTE_BPS)}/s critical</span>
      </div>

      <div style={{
        color: '#7af', margin: '6px 0 2px', letterSpacing: '0.08em',
        fontSize: 10, fontWeight: 600, display: 'flex', justifyContent: 'space-between',
      }}>
        <span>RECENT SPIKES</span>
        <span style={{ color: '#666', fontWeight: 400 }}>{spikes.length} captured</span>
      </div>

      {spikes.length === 0 ? (
        <div style={{ fontSize: 10, color: '#666', fontStyle: 'italic' }}>
          no spikes since mount — baseline only
        </div>
      ) : (
        <div style={{ fontSize: 10 }}>
          {spikes.map((s, i) => (
            <div key={`${s.ts}-${s.direction}`} style={{ marginTop: 3, paddingTop: 3, borderTop: i === 0 ? 'none' : '1px dotted #1f2330' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#888' }}>
                  {fmtRelTime(s.ts, now)} · {fmtTime(s.ts)}
                </span>
                <span>
                  <span style={{ color: s.direction === 'in' ? NETOBS_COLORS.DIR_IN : NETOBS_COLORS.DIR_OUT }}>
                    {s.direction === 'in' ? '↓' : '↑'}
                  </span>{' '}
                  <span style={{ color: '#ccc' }}>{fmtBytes(s.bytes)}</span>{' '}
                  <span style={{ color: TIER_COLORS[s.tier], fontWeight: 600 }}>
                    {s.ratio.toFixed(1)}× {s.tier}
                  </span>
                </span>
              </div>
              <div style={{ fontSize: 9, color: '#aaa', paddingLeft: 4 }}>
                {s.topTypes.map((t, ix) => (
                  <span key={t.type}>
                    {ix > 0 && <span style={{ color: '#444' }}> · </span>}
                    <span style={{ color: '#bbb' }}>{t.type.length > 18 ? t.type.slice(0, 17) + '…' : t.type}</span>
                    <span style={{ color: '#666' }}> {fmtBytes(t.bytes)}</span>
                    <span style={{ color: '#444' }}>×{t.msgs}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
